import type * as React from 'react'
import { Link } from '@tanstack/react-router'

const notFoundTokens = {
  '--not-found-space': '#142945',
  '--not-found-space-deep': '#10233d',
  '--not-found-planet-blue': '#11c5ff',
  '--not-found-planet-green': '#35ff84',
  '--not-found-cloud': '#cbe8ed',
  '--not-found-text': '#ffffff',
  '--not-found-focus': '#ffffff',
  '--not-found-motion': '200ms',
  fontFamily: 'Inter, Segoe UI, sans-serif',
} as React.CSSProperties

export function NotFoundPage() {
  return (
    <main
      className="min-h-full overflow-hidden bg-[radial-gradient(circle_at_center,var(--not-found-space)_0%,var(--not-found-space-deep)_70%)] text-[var(--not-found-text)]"
      style={notFoundTokens}
    >
      <section className="relative flex min-h-screen w-full items-center justify-center px-5 py-10">
        <OrbitGlow />
        <StarField />

        <div className="relative z-10 flex w-full max-w-5xl flex-col items-center">
          <div
            className="flex w-full items-center justify-center gap-8 sm:gap-12 lg:gap-16"
            aria-label="404"
          >
            <span className="select-none text-[8rem] font-black leading-none tracking-[-0.08em] text-white drop-shadow-[0_0_18px_rgb(255_255_255_/_0.55)] sm:text-[12rem] lg:text-[15rem]">
              4
            </span>
            <div className="relative">
              <EarthIcon />
              <CloudIcon className="absolute -left-7 top-6 w-28 sm:-left-10 sm:top-8 sm:w-36 lg:-left-14 lg:top-12 lg:w-44" />
            </div>
            <span className="select-none text-[8rem] font-black leading-none tracking-[-0.08em] text-white drop-shadow-[0_0_18px_rgb(255_255_255_/_0.55)] sm:text-[12rem] lg:text-[15rem]">
              4
            </span>
          </div>

          <p className="mt-12 text-center text-sm font-semibold tracking-wide text-white/85 sm:text-lg">
            You&apos;re lost in deep space.{' '}
            <Link
              className="border-b border-white pb-1 text-white transition-opacity duration-[var(--not-found-motion)] hover:opacity-70 focus-visible:outline-3 focus-visible:outline-offset-4 focus-visible:outline-[var(--not-found-focus)]"
              to="/super/login"
            >
              Let&apos;s go back.
            </Link>
          </p>
        </div>
      </section>
    </main>
  )
}

function OrbitGlow() {
  return (
    <>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_46%,rgb(42_91_132_/_0.34)_0%,rgb(42_91_132_/_0.2)_26%,transparent_58%)]" />
      <div className="absolute left-1/2 top-1/2 h-[72rem] w-[72rem] -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/[0.015]" />
      <div className="absolute left-1/2 top-1/2 h-[52rem] w-[52rem] -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/[0.025]" />
      <div className="absolute left-1/2 top-1/2 h-[34rem] w-[34rem] -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/[0.035]" />
    </>
  )
}

function StarField() {
  const stars = [
    ['8%', '18%', '1px'],
    ['18%', '4%', '2px'],
    ['28%', '18%', '1px'],
    ['43%', '5%', '1px'],
    ['64%', '9%', '4px'],
    ['76%', '20%', '1px'],
    ['88%', '6%', '1px'],
    ['95%', '24%', '4px'],
    ['13%', '64%', '3px'],
    ['27%', '66%', '2px'],
    ['36%', '86%', '3px'],
    ['45%', '69%', '4px'],
    ['58%', '74%', '1px'],
    ['71%', '88%', '2px'],
    ['87%', '72%', '4px'],
    ['94%', '78%', '3px'],
    ['23%', '93%', '2px'],
    ['74%', '58%', '1px'],
    ['82%', '43%', '1px'],
    ['7%', '86%', '1px'],
  ] as const

  return (
    <div className="absolute inset-0">
      {stars.map(([left, top, size]) => (
        <span
          className="absolute rotate-45 bg-white shadow-[0_0_10px_rgb(255_255_255_/_0.8)]"
          key={`${left}-${top}`}
          style={{
            left,
            top,
            width: size,
            height: size,
          }}
        />
      ))}
    </div>
  )
}

function EarthIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-40 w-40 drop-shadow-[0_0_36px_rgb(24_212_255_/_0.42)] sm:h-60 sm:w-60 lg:h-72 lg:w-72"
      viewBox="0 0 260 260"
    >
      <defs>
        <clipPath id="earth-clip">
          <circle cx="130" cy="130" r="104" />
        </clipPath>
        <linearGradient id="earth-blue" x1="55" x2="198" y1="35" y2="229">
          <stop stopColor="#0ff4ff" />
          <stop offset="1" stopColor="#0a9cff" />
        </linearGradient>
      </defs>
      <circle cx="130" cy="130" fill="url(#earth-blue)" r="104" />
      <g clipPath="url(#earth-clip)" fill="var(--not-found-planet-green)">
        <g>
          <animateTransform
            attributeName="transform"
            dur="5s"
            from="0 0"
            repeatCount="indefinite"
            to="220 0"
            type="translate"
          />
          {[-220, 0, 220].map((offset) => (
            <g key={offset} transform={`translate(${offset} 0)`}>
              <path d="M53 92c9-33 35-58 69-66 5 15 0 29-15 37-20 11-25 25-16 41 7 13 1 23-15 29-16 7-27 0-33-19-3-8 1-15 10-22Z" />
              <path d="M137 30c41 3 76 31 90 69-17 5-34 0-50-16-13-12-24-15-34-8-10 6-21 5-34-3 15-10 24-24 28-42Z" />
              <path d="M83 158c13-14 29-19 49-14 18 5 31 16 39 34 5 12 2 24-9 36l-31 34c-37-1-68-22-87-53 14-9 27-21 39-37Z" />
              <path d="M186 121c19 5 35 15 49 30-5 29-21 54-44 70-6-17-5-31 4-42 10-12 9-24-1-36-6-7-9-15-8-22Z" />
            </g>
          ))}
        </g>
      </g>
      <path
        d="M49 79c-16 71 28 134 94 150"
        fill="none"
        opacity="0.18"
        stroke="#001e46"
        strokeLinecap="round"
        strokeWidth="30"
      />
      <circle
        cx="130"
        cy="130"
        fill="none"
        opacity="0.18"
        r="104"
        stroke="#ffffff"
        strokeWidth="3"
      />
    </svg>
  )
}

function CloudIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" className={className} fill="none" viewBox="0 0 190 88">
      <path
        d="M47 72c-19 0-34-12-34-28 0-15 12-27 28-29 8-13 23-18 39-14 13 3 23 11 28 23 5-2 11-3 18-3 27 0 49 21 49 47v4H47Z"
        fill="var(--not-found-cloud)"
        opacity="0.95"
      />
      <path
        d="M8 67c0-9 8-16 18-16h115c12 0 23-5 31-13 2 5 3 10 3 16 0 20-17 34-40 34H30C17 88 8 79 8 67Z"
        fill="#b4dce5"
      />
    </svg>
  )
}
